import type { Aggregates, AreaId } from '../types/pulse'
import { followUps } from '../data/pulse'
import { followUpRows, rankFirstRows, ratingStats, sortedThemes } from './pulse'

export interface InterpretationSection {
  id: string
  title: string
  paragraphs: string[]
  bullets?: string[]
}

export interface Interpretation {
  generatedAt: string
  sampleSize: number
  sections: InterpretationSection[]
}

const AREA_EMPHASIS: Record<AreaId, string[]> = {
  diagnosis: [
    'Precision and patient characterisation',
    'Getting more out of imaging already performed',
    'Access — reaching specialist care sooner',
  ],
  treatment: [
    'Treatment burden for patient and clinic',
    'Patient outcomes and consistency of benefit',
    'Clinic sustainability and capacity',
  ],
  followup: [
    'Follow-up efficiency and prioritisation',
    'Capacity — protecting specialist time',
    'Workforce and delegated monitoring',
  ],
}

const AREA_ROLE: Record<AreaId, string> = {
  diagnosis:
    'a scientific and education partner around characterising patients and interpreting the data clinics already generate',
  treatment:
    'a treatment partner focused on practical burden and outcome consistency, rather than a systems or technology lead',
  followup:
    'a system-support partner around pathway design, prioritisation and delegated monitoring',
}

const SUB_THEME_READ: Record<string, string> = {
  'diagnosis:earlier': 'earlier identification of patients needing specialist care',
  'diagnosis:precise': 'more precise characterisation of the individual patient',
  'diagnosis:imaging': 'extracting more value from imaging and data already collected',
  'treatment:earlier': 'getting patients onto the right treatment with less trial-and-error',
  'treatment:outcomes': 'stronger and more consistent clinical benefit',
  'treatment:burden': 'reducing treatment, procedure and visit burden',
  'followup:prioritise': 'directing specialist attention to the patients who need it most',
  'followup:efficient': 'monitoring appropriate patients without consuming specialist time',
  'followup:change': 'recognising deterioration or the need to reassess earlier',
}

function pctOf(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0
}

/**
 * Turns the live aggregate into a directional, hypothesis-shaped read for
 * internal users. Every statement is derived from the data in hand — nothing
 * is asserted that the responses do not contain.
 */
export function buildInterpretation(agg: Aggregates): Interpretation {
  const journey = rankFirstRows(agg, null)
  const leader = journey[0]
  const runnerUp = journey[1]
  const areaId = leader.id as AreaId
  const spread = leader.pct - runnerUp.pct

  const subRows = followUpRows(agg, areaId, null)
  const structured = subRows.filter((r) => r.id !== 'other')
  const topSub = structured[0]
  const otherRow = subRows.find((r) => r.id === 'other')
  const subRead = SUB_THEME_READ[`${areaId}:${topSub.id}`] ?? topSub.label.toLowerCase()

  const team = ratingStats(agg.team)
  const tech = ratingStats(agg.tech)
  const teamAvg = Number(team.average)
  const techAvg = Number(tech.average)

  const themes = sortedThemes(agg)
  const topThemes = themes.slice(0, 3)
  const themeNames = topThemes.map((t) => t.label.toLowerCase())
  const burdenWeight = themes.find((t) => t.id === 'burden')?.count ?? 0
  const capacityWeight = themes.find((t) => t.id === 'capacity')?.count ?? 0
  const adminWeight = themes.find((t) => t.id === 'admin')?.count ?? 0

  // ---- Section 1 — what the data is saying -------------------------------
  const signals: string[] = []
  signals.push(
    `Delegates currently place the greatest opportunity in ${leader.label.toLowerCase()} (${leader.pct}% rank it #1), ${
      spread >= 12
        ? `clearly ahead of ${runnerUp.label.toLowerCase()} on ${runnerUp.pct}%`
        : `only narrowly ahead of ${runnerUp.label.toLowerCase()} on ${runnerUp.pct}% — the room is close to evenly split`
    }. Within that area, ${subRead} is the strongest sub-signal at ${topSub.pct}%.`,
  )
  signals.push(
    `Both operational questions score above the midpoint — wider care-team models at ${team.average}/5 and technology at ${tech.average}/5 — with ${
      teamAvg >= techAvg
        ? 'workforce models currently rated at least as highly as technology'
        : 'technology currently rated slightly higher than workforce models'
    }. Free-text responses cluster around ${themeNames.join(', ')}, which ${
      capacityWeight >= burdenWeight
        ? 'points more at time and capacity pressure than at any single clinical gap'
        : 'points at the practical burden of care rather than at a single clinical gap'
    }.`,
  )

  const openTotal = agg.openResponses
  const themeBullets = topThemes.map(
    (t) => `${t.label} — roughly ${pctOf(t.count, openTotal)}% of open responses (${t.count})`,
  )
  if (otherRow && otherRow.pct >= 10) {
    themeBullets.push(
      `${otherRow.pct}% chose "Something else" in the follow-up question — an unexpected signal worth reading verbatim before grouping`,
    )
  }

  // ---- Section 2 — messaging implications --------------------------------
  const emphasis = [...AREA_EMPHASIS[areaId]]
  if (techAvg >= 4 && (adminWeight >= 8 || burdenWeight >= 12)) {
    emphasis.push('Implementation effort — interest in technology sits alongside complexity worries')
  }
  if (teamAvg >= 4) emphasis.push('Team-based models and task sharing')

  const messaging: string[] = [
    `The pattern may suggest that messaging anchored in ${subRead} and everyday clinic reality could resonate more strongly than ${
      techAvg > teamAvg ? 'abstract innovation framing' : 'technology-first framing'
    }. ${
      techAvg >= 4
        ? `Technology is clearly of interest (${tech.highPct}% rate it 4–5), but the free-text signals suggest it may land better as an enabler of ${leader.label.toLowerCase()} than as a topic in itself.`
        : `Technology scores more modestly (${tech.average}/5), so leading with it may be premature for this audience.`
    }`,
  ]

  // ---- Section 3 — potential AbbVie role ---------------------------------
  const roles: string[] = [
    `The current data may indicate AbbVie has most permission to act as ${AREA_ROLE[areaId]}.`,
  ]
  if (teamAvg >= 4) {
    roles.push(
      'The strong wider-care-team rating could also support a connector-of-peer-knowledge role — convening how colleagues are redesigning pathways, rather than proposing a model.',
    )
  }
  if (techAvg >= 4 && teamAvg >= 4) {
    roles.push(
      'Where both workforce and technology score highly, an innovation-facilitator role could be considered — but the data suggests facilitating, not leading.',
    )
  }

  // ---- Section 4 — content and field-force -------------------------------
  const fieldForce: string[] = [
    `Congress content and future Snaks could be built around ${subRead}, with ${leader.label.toLowerCase()} as the organising frame.`,
    `Reps may gain more engagement by opening on ${themeNames[0]} and clinic pressure than on innovation or product capability.`,
  ]
  if (areaId === 'diagnosis') {
    fieldForce.push(
      'Imaging and characterisation content could be tested as peer-to-peer material rather than as promotional detail.',
    )
  }
  if (areaId === 'treatment') {
    fieldForce.push(
      'Treatment-pathway discussions could lead with visit and procedure burden before efficacy framing.',
    )
  }
  if (areaId === 'followup') {
    fieldForce.push(
      'Pathway-redesign and delegated-monitoring formats could be piloted with a small number of interested centres.',
    )
  }

  // ---- Cross-question patterns -------------------------------------------
  const crossPatterns: string[] = []
  if (areaId === 'diagnosis' && topSub.id === 'imaging' && techAvg >= 4) {
    crossPatterns.push(
      'Diagnosis ranked highest + imaging/data selected + high technology score → possible interest in precision and imaging-led content.',
    )
  }
  if (areaId === 'treatment' && topSub.id === 'burden' && burdenWeight >= 10) {
    crossPatterns.push(
      'Treatment ranked highest + reducing burden selected + free text on visits and capacity → possible emphasis on treatment burden and service sustainability.',
    )
  }
  if (areaId === 'followup' && teamAvg >= 4) {
    crossPatterns.push(
      'Follow-up ranked highest + high care-team score → possible interest in pathway redesign and delegated monitoring.',
    )
  }
  if (techAvg >= 4 && adminWeight >= 8) {
    crossPatterns.push(
      'High technology score alongside free-text comments about administration → interest exists, but implementation burden may be a barrier.',
    )
  }
  if (Math.abs(teamAvg - techAvg) <= 0.2) {
    crossPatterns.push(
      'Workforce and technology score almost identically → delegates may see them as one combined capacity question rather than two options.',
    )
  }

  return {
    generatedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    sampleSize: agg.team.n,
    sections: [
      {
        id: 'signals',
        title: 'What the data is saying',
        paragraphs: signals,
        bullets: themeBullets,
      },
      {
        id: 'messaging',
        title: 'Potential messaging implications',
        paragraphs: messaging,
        bullets: emphasis,
      },
      { id: 'role', title: 'Potential AbbVie role', paragraphs: roles },
      {
        id: 'field',
        title: 'Potential content and field-force implications',
        paragraphs: fieldForce,
        bullets: crossPatterns.length ? crossPatterns : undefined,
      },
      {
        id: 'cautions',
        title: 'Important cautions',
        paragraphs: [
          'These findings should be treated as directional signals rather than definitive market conclusions.',
        ],
        bullets: [
          'This is a self-selected congress sample — delegates who chose to respond at the stand.',
          'Results are directional and not representative of all Nordic ophthalmologists.',
          'Country is not captured in this pulse, so no country breakdown is available; small subsamples should never be overinterpreted.',
          'Patterns across questions are associations only — they do not establish causation.',
          'This interpretation is a strategic hypothesis, not evidence of HCP preference beyond the responses collected.',
        ],
      },
    ],
  }
}
